'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Box, Container, Typography } from '@mui/material'

import useRedirectToHome from '@/src/hooks/navigation/useRedirectToHome'
import { useTripList } from '@/src/hooks/trips/useTripList'
import FullPageLoaderWrapper from '@/src/components/desktop/fullPageLoader'
import { TripListCategory } from '@/src/types/trips'
import { HeaderContainer } from '@/src/components/common/header/HeaderRouter'
import { AccountsSidePanelWrapper } from '@/src/components/desktop/accounts/sidePanel'
import { getStatusTone, formatTripDateParts, formatTripTitleParts } from '@/src/utils/trips/tripListHelpers'
import { getHotelTripDetailsUrl, getTripDetailsUrl } from '@/src/utils/tripDetails/urls'
import { TripListDesktop, TripListCard } from './tripList'
import { useAppDispatch } from '@/src/redux/store'
import { clearAuthUser, refreshAuthUser } from '@/src/redux/slices/authSlice'
import { useUserLogoutMutation } from '@/src/api/rtkQuery/userApi'
import { getBrandName } from '@/src/utils/buildUtils'
import { deleteCookie } from '@/src/utils/browser'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

const TripListDesktopPageWrapper: React.FC<{ domainCountry: string; isMultiBrand?: boolean }> = ({
    domainCountry,
    isMultiBrand: isMultiBrandSsr
}) => {
    const dispatch = useAppDispatch()
    const isMultiBrand = isMultiBrandSsr ?? false
    const router = useRouter()
    const t = useTranslations('tripList')
    const locale = useLocale()
    const goToHome = useRedirectToHome()

    const [isMounted, setIsMounted] = useState(false)
    const [activeTab, setActiveTab] = useState<TripListCategory>('upcoming')
    const [isSignInOpen, setIsSignInOpen] = useState(false)
    const [triggerLogout] = useUserLogoutMutation()
    const hasHandledUnauthorizedRef = useRef(false)
    const scrollPositionRef = useRef<number>(0)
    const previousCardsLengthRef = useRef<number>(0)

    const {
        isLoggedIn,
        isLoading,
        isFetching,
        isError,
        hasUnauthorizedError,
        tripsByCategory,
        tripCounts,
        hasMore,
        loadMore,
        isLoadingMore,
        refetch
    } = useTripList({
        isMultiBrand
    })

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    useEffect(() => {
        if (!isMounted || !isLoggedIn || !hasUnauthorizedError || hasHandledUnauthorizedRef.current) return
        hasHandledUnauthorizedRef.current = true

        const forceSignOutOnUnauthorized = async () => {
            try {
                await triggerLogout({ brand: getBrandName() }).unwrap()
            } catch (error) {
                console.error('Trip list unauthorized logout failed:', error)
            } finally {
                dispatch(clearAuthUser())
                if (typeof window !== 'undefined') {
                    const domain = '.' + window.location.hostname
                    deleteCookie('usermisc', domain)
                    deleteCookie('userid', domain)
                    deleteCookie('userData', domain)
                }
                dispatch(refreshAuthUser())
                setIsSignInOpen(true)
            }
        }

        void forceSignOutOnUnauthorized()
    }, [dispatch, hasUnauthorizedError, isLoggedIn, isMounted, triggerLogout])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [isLoggedIn, router, locale])

    const handleGoHome = useCallback(() => {
        goToHome({ isReloadRequired: false })
    }, [goToHome])

    const dateUnknownLabel = t('dateUnknown')

    const cards = useMemo(() => {
        const selectedTrips = tripsByCategory[activeTab] || []
        return selectedTrips.map((trip): TripListCard => {
            const dateParts = formatTripDateParts(trip.travelDate, locale, dateUnknownLabel)
            return {
                id: trip.tripId,
                tripId: trip.tripId,
                tripType: trip.tripType,
                titleParts: formatTripTitleParts(trip, t('untitledTrip', { tripId: trip.tripId })),
                subtitle: trip.travellers || t('travellerFallback'),
                bookedByLabel: trip.bookingSource === 'LINKED' ? t('bookedByCleartrip') : undefined,
                bookingSource: trip.bookingSource,
                dateDay: dateParts.dateDay,
                dateMonth: dateParts.dateMonth,
                dateLabel: dateParts.dateLabel,
                statusLabel: trip.bookingStatus || '',
                statusTone: getStatusTone(trip.bookingStatus)
            }
        })
    }, [activeTab, tripsByCategory, locale, dateUnknownLabel, t])

    useEffect(() => {
        if (
            !isLoadingMore[activeTab] &&
            previousCardsLengthRef.current > 0 &&
            cards.length > previousCardsLengthRef.current &&
            scrollPositionRef.current > 0
        ) {
            const timeoutId = setTimeout(() => {
                if (typeof window !== 'undefined') {
                    window.scrollTo({
                        top: scrollPositionRef.current,
                        behavior: 'auto'
                    })
                    scrollPositionRef.current = 0
                    previousCardsLengthRef.current = 0
                }
            }, 100)

            return () => clearTimeout(timeoutId)
        }
    }, [isLoadingMore, activeTab, cards.length])

    const tabConfig = useMemo(
        () => [
            { value: 'upcoming' as TripListCategory, label: t('tabs.upcoming') },
            { value: 'past' as TripListCategory, label: t('tabs.past') }
        ],
        [t]
    )

    const handleRetry = useCallback(() => {
        refetch()
    }, [refetch])

    const handleCardClick = useCallback((tripId: string, bookingSource?: 'MAIN' | 'LINKED', tripType?: string) => {
        if (tripId) {
            const normalizedTripType = (tripType || '').toLowerCase().trim()
            const isAirTrip = normalizedTripType === 'air'
            window.location.href = isAirTrip
                ? getTripDetailsUrl(tripId, locale, bookingSource)
                : getHotelTripDetailsUrl(tripId, locale, bookingSource)
        }
    }, [locale])

    const handleLoadMore = useCallback(() => {
        if (typeof window !== 'undefined') {
            scrollPositionRef.current = window.scrollY || window.pageYOffset
            previousCardsLengthRef.current = cards.length
        }
        loadMore(activeTab)
    }, [activeTab, loadMore, cards.length])

    if (!isMounted) return null

    // Full-page loader only when initial load (no cards yet). When loading more, keep showing list + bottom shimmer.
    const showFullPageLoader = isLoading && isLoggedIn && cards.length === 0
    const showListSkeleton = isLoading && !isLoadingMore[activeTab]

    if (showFullPageLoader) {
        return (
            <>
                <FullPageLoaderWrapper
                    hideBackdrop
                    message={
                        <>
                            <Typography
                                variant="body1"
                                sx={{ color: 'text.primary', textAlign: 'center', fontWeight: 500 }}
                            >
                                {t('fetchingTrips')}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center' }}>
                                {t('fetchingTripsMessage')}
                            </Typography>
                        </>
                    }
                />
                {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
            </>
        )
    }

    return (
        <Box
            sx={{
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                bgcolor: 'background.default'
            }}
        >
            <HeaderContainer domainCountry={domainCountry} />

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', bgcolor: 'grey.50' }}>
                <Container
                    maxWidth="xl"
                    disableGutters
                    sx={{
                        height: '100%',
                        py: { xs: 2, md: 3 },
                        px: { xs: 2, md: 3 }
                    }}
                >
                    <Box
                        sx={{
                            height: '100%',
                            display: 'flex',
                            gap: { xs: 0, md: 1 },
                            alignItems: { xs: 'flex-start', md: 'stretch' }
                        }}
                    >
                        <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 3 }}>
                            <AccountsSidePanelWrapper activeIndex={0} />
                        </Box>

                        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                            <TripListDesktop
                                heading={t('title')}
                                tabs={tabConfig}
                                activeTab={activeTab}
                                onTabChange={setActiveTab}
                                cards={cards}
                                isLoading={showListSkeleton}
                                isError={isError}
                                errorLabel={t('error')}
                                retryLabel={t('retry')}
                                onRetry={handleRetry}
                                emptyState={{
                                    title: isLoggedIn ? t('empty.title') : t('notSignedIn.title'),
                                    description: isLoggedIn ? t('empty.description') : t('notSignedIn.description'),
                                    actionLabel: isLoggedIn ? t('empty.cta') : t('notSignedIn.cta'),
                                    onAction: isLoggedIn ? handleGoHome : () => setIsSignInOpen(true)
                                }}
                                onCardClick={handleCardClick}
                                hasMore={hasMore[activeTab]}
                                isLoadingMore={isLoadingMore[activeTab]}
                                onLoadMore={handleLoadMore}
                                loadMoreLabel={t('loadMore')}
                                loadingLabel={t('loading')}
                            />
                        </Box>
                    </Box>
                </Container>
            </Box>

            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </Box>
    )
}

export { TripListDesktopPageWrapper }
export default TripListDesktopPageWrapper
